/* Flash sale code button on the product page.
   Applies the code through the Cart AJAX API without leaving the page, then
   reports what the cart actually did with it. The link's own href is the
   fallback where this never runs. */
(() => {
  if (customElements.get('flash-promo')) return;

  const routes = window.routes || {};
  const cartJsUrl = () => `${routes.cart_url || '/cart'}.js`;
  const updateJsUrl = () => `${routes.cart_update_url || '/cart/update'}.js`;
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  class FlashPromo extends HTMLElement {
    connectedCallback() {
      this.code = (this.dataset.code || '').trim();
      this.button = this.querySelector('[data-flash-apply]');
      this.label = this.querySelector('[data-flash-label]');
      this.status = this.querySelector('[data-flash-status]');
      if (!this.code || !this.button || !this.label || !this.status) return;

      this.onClick = (event) => {
        event.preventDefault();
        this.apply();
      };
      this.button.addEventListener('click', this.onClick);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        /* Adding the product to the basket can turn a saved code into an applied one. */
        this.unsubscribers = [subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh())];

        /* The panel is outside the block the theme repaints, so take the new price from the section HTML it fetched. */
        if (PUB_SUB_EVENTS.variantChange) {
          this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.updatePrice(event)));
        }
      }

      this.renderSticky();
      this.refresh();
    }

    disconnectedCallback() {
      if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
      if (this.stickyApply) this.stickyApply.remove();
      const section = this.closest('.shopify-section') || document;
      const line = section.querySelector('.main-product__sticky-info .flash-promo-sticky');
      if (line) line.remove();
      (this.unsubscribers || []).forEach((unsubscribe) => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });
    }

    updatePrice(event) {
      const html = event && event.data && event.data.html;
      if (!html || typeof html.querySelector !== 'function') return;
      const fresh = html.querySelector(`flash-promo[data-product-id="${this.dataset.productId}"] [data-flash-price]`);
      const slot = this.querySelector('[data-flash-price]');
      if (fresh && slot) slot.innerHTML = fresh.innerHTML;
      this.renderSticky();
    }

    /* The sticky product bar is what most mobile buyers see, and the theme builds it from the regular price only. */
    renderSticky() {
      const section = this.closest('.shopify-section') || document;
      const sticky = section.querySelector('.main-product__sticky-info');
      if (!sticky) return;

      const stickyPrice = sticky.querySelector('[id^="price-sticky-"]');
      const amount = this.querySelector('[data-flash-price] .flash-promo__amount');
      let line = sticky.querySelector('.flash-promo-sticky');
      let apply = sticky.querySelector('.flash-promo-sticky-apply');

      if (!stickyPrice || !amount) {
        if (line) line.remove();
        if (apply) apply.remove();
        this.stickyApply = null;
        return;
      }

      if (!line) {
        line = document.createElement('div');
        line.className = 'flash-promo-sticky';
        ['flag', 'amount', 'label'].forEach((part) => {
          const span = document.createElement('span');
          span.className = `flash-promo-sticky__${part}`;
          line.append(span);
        });
        stickyPrice.insertAdjacentElement('afterend', line);
      }

      const flagSource = this.querySelector('.promo-line__flag');
      line.querySelector('.flash-promo-sticky__flag').textContent = flagSource ? flagSource.textContent.trim() : '';
      line.querySelector('.flash-promo-sticky__amount').textContent = amount.textContent.trim();
      line.querySelector('.flash-promo-sticky__label').textContent = this.dataset.stickyLabel || '';

      const buttons = sticky.querySelector('.main-product__sticky-add-to-cart .product-form__buttons');
      if (!apply && buttons) {
        apply = document.createElement('button');
        apply.type = 'button';
        apply.className = 'flash-promo-sticky-apply';
        apply.setAttribute('aria-label', `Apply discount code ${this.code}`);
        apply.addEventListener('click', () => this.apply());
        buttons.insertAdjacentElement('beforebegin', apply);
      }
      this.stickyApply = apply || null;
      this.syncSticky();
    }

    syncSticky() {
      const apply = this.stickyApply;
      if (!apply) return;

      const done = this.state === 'applied' || this.state === 'saved';
      apply.dataset.state = this.state || '';
      apply.setAttribute('aria-disabled', done ? 'true' : 'false');
      if (this.busy) apply.setAttribute('aria-busy', 'true');
      else apply.removeAttribute('aria-busy');

      if (done) apply.textContent = this.dataset.labelDone || this.code;
      else if (this.state === 'failed') apply.textContent = 'Try again';
      else apply.textContent = `Apply ${this.code}`;

      if (this.state === 'failed') apply.title = this.dataset.msgFailed || '';
      else apply.removeAttribute('title');
    }

    find(cart) {
      const wanted = this.code.toUpperCase();
      const codes = (cart && Array.isArray(cart.discount_codes)) ? cart.discount_codes : [];
      return codes.find((entry) => String(entry.code).toUpperCase() === wanted);
    }

    async getCart() {
      const response = await fetch(cartJsUrl(), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`cart ${response.status}`);
      return response.json();
    }

    /* Shows the right state on load, so a buyer who applied the code on another product sees it here too. */
    async refresh() {
      if (this.busy) return;
      try {
        const found = this.find(await this.getCart());
        if (found) this.show(found.applicable ? 'applied' : 'saved');
      } catch (error) {
        /* Leave the button as it is. */
      }
    }

    async apply() {
      if (this.busy || this.state === 'applied' || this.state === 'saved') return;
      this.busy = true;
      this.button.setAttribute('aria-busy', 'true');
      this.status.textContent = '';
      this.syncSticky();

      try {
        /* Keep any code already on the basket rather than replacing it. */
        const current = await this.getCart();
        const wanted = this.code.toUpperCase();
        const others = (Array.isArray(current.discount_codes) ? current.discount_codes : [])
          .map((entry) => String(entry.code))
          .filter((code) => code.toUpperCase() !== wanted);

        const response = await fetch(updateJsUrl(), {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({ discount: [...others, this.code].join(',') }),
        });
        if (!response.ok) throw new Error(`update ${response.status}`);

        const found = this.find(await response.json());
        if (!found) throw new Error('code not on cart');
        this.show(found.applicable ? 'applied' : 'saved');
      } catch (error) {
        this.show('failed');
      } finally {
        this.busy = false;
        this.button.removeAttribute('aria-busy');
        this.syncSticky();
      }
    }

    show(state) {
      this.state = state;
      this.dataset.state = state;

      const key = `msg${state.charAt(0).toUpperCase()}${state.slice(1)}`;
      this.status.textContent = this.dataset[key] || '';

      const done = state === 'applied' || state === 'saved';
      this.button.setAttribute('aria-disabled', done ? 'true' : 'false');
      if (done && this.dataset.labelDone) this.label.textContent = this.dataset.labelDone;
      this.syncSticky();
    }
  }

  customElements.define('flash-promo', FlashPromo);
})();
